/**
 * Marketing / attribution query params read from the **host page** URL for Pathfinder and parity with site tags.
 */
export interface TrackingParams {
  rsid: string;
  subId: string;
  track1: string;
  revenueCode: string;
}

/** Normalizes `URLSearchParams` values (including repeated keys) to a single string. */
function getParamAsSingleString(val: string | string[] | null, defaultValue = ''): string {
  if (val == null) return defaultValue;
  if (Array.isArray(val)) {
    return val.length > 0 && typeof val[0] === 'string' ? val[0] : defaultValue;
  }
  return typeof val === 'string' ? val : defaultValue;
}

/** First non-empty `subId` / `subid` value (case-insensitive key match). */
function getSubIdFromSearchParams(params: URLSearchParams): string {
  for (const [k, v] of params) {
    if (k.toLowerCase() === 'subid' && v) return v;
  }
  return '';
}

/**
 * Query segment after `?` inside the hash (e.g. `#/article?subid=x`, `#?subid=x`) or a simple `#subid=x` fragment.
 */
function extractQueryStringFromHash(hash: string): string {
  if (!hash) return '';
  const withoutHash = hash.startsWith('#') ? hash.slice(1) : hash;
  const q = withoutHash.indexOf('?');
  if (q >= 0) return withoutHash.slice(q + 1);
  if (withoutHash.includes('=') && !withoutHash.includes('/')) {
    return withoutHash;
  }
  return '';
}

/**
 * `subid` from the current page URL: `?subid=` / `?subId=` on `location.search`, then the same keys inside
 * `location.hash` when SPAs keep params in the fragment.
 */
export function getSubIdFromPageUrl(): string {
  if (typeof window === 'undefined') return '';
  const fromSearch = getSubIdFromSearchParams(new URLSearchParams(window.location.search));
  if (fromSearch) return fromSearch;
  const hashQuery = extractQueryStringFromHash(window.location.hash);
  if (!hashQuery) return '';
  return getSubIdFromSearchParams(new URLSearchParams(hashQuery));
}

/** Parses the current window search string; returns empty strings when absent or in SSR. */
export function getTrackingParamsFromUrl(): TrackingParams {
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const get = (key: string, altKey?: string): string => {
    const val = params.get(key) ?? (altKey ? params.get(altKey) : null);
    return getParamAsSingleString(val ?? '', '');
  };

  return {
    rsid: get('rsid'),
    subId: getSubIdFromPageUrl(),
    track1: get('track1'),
    revenueCode: get('revenueCode', 'revCode'),
  };
}
